// salary-calculator-tool

// Methodology.js
import React from 'react';
import VariableTable from './VariableTable';
import { Typography, Alert, Collapse, Divider, Row, Col, Tag, Button, Space, Tabs } from 'antd';
import { CalculatorOutlined, ProfileOutlined, AreaChartOutlined, InsuranceOutlined, ReadOutlined, ThunderboltOutlined, ExperimentOutlined } from '@ant-design/icons';
import SectionCard from './components/methodology/SectionCard';
import BandMeter from './components/methodology/BandMeter';
import BandTable from './components/methodology/BandTable';
import ExampleBlock from './components/methodology/ExampleBlock';
import FormulaBlock from './components/methodology/FormulaBlock';
import StepList from './components/methodology/StepList';

const { Title, Paragraph, Text } = Typography;

function Methodology({ selectedTaxYear = "2024/2025", isTaxResidentInScotland = false }) {
  const copyToWizard = (preset) => {
    try {
      const event = new CustomEvent('wizard:applyPreset', { detail: preset });
      window.dispatchEvent(event);
    } catch (_) {}
  };

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Title level={2} style={{ marginBottom: 0 }}>Methodology</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Paragraph type="secondary" style={{ margin: 0 }}>
                Based on {selectedTaxYear} tax year rates. Supports England/Wales/NI and Scotland regimes.
              </Paragraph>
              <Tag color={isTaxResidentInScotland ? 'green' : 'blue'}>
                {isTaxResidentInScotland ? 'Scotland' : 'UK (E/W/NI)'}
              </Tag>
            </div>
          </Space>
          <Alert
            type="info"
            showIcon
            message={<Text strong>TL;DR</Text>}
            description={
              <ul style={{ marginLeft: 18, marginBottom: 0 }}>
                <li>We compute taxable income excl/incl RSUs and apply personal allowance taper automatically.</li>
                <li>Income Tax and NIC are banded step-wise, using the selected regime for bands and thresholds.</li>
                <li>Salary sacrifice reduces taxable base; P11D benefits add to taxable income.</li>
                <li>RSU withholding vs actual liability is explained and reconciled at year end.</li>
              </ul>
            }
            style={{ marginTop: 12, marginBottom: 12 }}
          />
        </Col>

        <Col xs={24} lg={16}>
          <Tabs
            defaultActiveKey="taxable"
            items={[
              {
                key: 'taxable',
                label: (
                  <span><CalculatorOutlined /> Taxable income</span>
                ),
                children: (
                  <SectionCard id="taxable-income" title="How your taxable income is derived" subtitle="Excluding/including RSUs; personal allowance taper">
                    <FormulaBlock
                      lines={[
                        'Taxable Income (excl RSU) = Base Salary + Bonus + Medical Benefit − Employee Pension Contribution',
                        'Taxable Income (incl RSU) = Taxable Income (excl RSU) + RSUs subject to income tax',
                        'RSUs subject to income tax = RSU amount − Employer NIC (if applicable)',
                      ]}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <StepList steps={[
                      'Enter base salary, bonus, medical benefit and pension contribution',
                      'Review taxable income excluding RSUs',
                      'If you have RSUs, include them to see total taxable and personal allowance impact',
                    ]} />
                    <Divider style={{ margin: '12px 0' }} />
                    <ExampleBlock
                      description="Worked example"
                      exampleLines={[
                        'Base £50,000; Bonus £10,000; Medical £600; Pension 10% => Employee Pension £5,000',
                        'Excl RSU Taxable = 50,000 + 10,000 + 600 − 5,000 = £55,600',
                        'RSU £10,000; Employer NIC 6.9% => RSU subject to tax £9,310',
                        'Incl RSU Taxable = £64,910',
                      ]}
                      onCopy={() => copyToWizard({ baseSalary: 50000, bonus: 10000, medicalBenefit: 600, employeePensionContribution: 10, rsus: 10000, rsuemployerNICrate: 6.9 })}
                    />
                  </SectionCard>
                ),
              },
              {
                key: 'tax',
                label: (
                  <span><ProfileOutlined /> Income tax</span>
                ),
                children: (
                  <SectionCard id="income-tax" title="How income tax is applied" subtitle={isTaxResidentInScotland ? 'Scottish bands' : 'UK bands'}>
                    <BandMeter
                      bands={isTaxResidentInScotland ? [
                        { color: '#d1fae5', widthPct: 16, label: 'Starter 19%' },
                        { color: '#bbf7d0', widthPct: 16, label: 'Basic 20%' },
                        { color: '#a7f3d0', widthPct: 20, label: 'Intermediate 21%' },
                        { color: '#86efac', widthPct: 26, label: 'Higher 42%' },
                        { color: '#6ee7b7', widthPct: 22, label: 'Top 48%' },
                      ] : [
                        { color: '#dbeafe', widthPct: 30, label: 'Basic 20%' },
                        { color: '#bfdbfe', widthPct: 55, label: 'Higher 40%' },
                        { color: '#93c5fd', widthPct: 15, label: 'Additional 45%' },
                      ]}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <BandTable taxYear={selectedTaxYear} isScottish={isTaxResidentInScotland} />
                    <Divider style={{ margin: '12px 0' }} />
                    <StepList steps={[
                      'Start from taxable income after personal allowance',
                      'Apply rates sequentially by band up to the upper limit',
                      'Any remainder beyond the last band is taxed at the top band',
                    ]} />
                  </SectionCard>
                ),
              },
              {
                key: 'nic',
                label: (
                  <span><InsuranceOutlined /> National Insurance</span>
                ),
                children: (
                  <SectionCard id="nic" title="National Insurance (NIC)">
                    <FormulaBlock
                      lines={[
                        'NIC Lower band = 8% on income between PT and UEL (UK 24/25)',
                        'NIC Upper band = 2% on income above UEL',
                      ]}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <Paragraph>Check how RSU timing can push months into higher NIC bands.</Paragraph>
                  </SectionCard>
                ),
              },
              {
                key: 'rsu',
                label: (
                  <span><ReadOutlined /> RSUs</span>
                ),
                children: (
                  <SectionCard id="rsu" title="RSUs explained (withholding vs actual)">
                    <Paragraph>
                      Employers often withhold at a flat rate (e.g., 50%). Actual liability depends on your total income.
                      The difference reconciles via Self-Assessment after tax year end.
                    </Paragraph>
                    <Tag color="blue">Tip</Tag> Compare results with RSUs toggled off vs on.
                  </SectionCard>
                ),
              },
              {
                key: 'loans',
                label: (
                  <span><ExperimentOutlined /> Student loans</span>
                ),
                children: (
                  <SectionCard id="student-loans" title="Student loans">
                    <FormulaBlock
                      lines={[
                        'Undergrad plans (1/2/4/5) = 9% of income above plan threshold',
                        'Postgraduate = 6% of income above threshold (concurrent with one undergrad plan)',
                      ]}
                    />
                  </SectionCard>
                ),
              },
              {
                key: 'benefits',
                label: (
                  <span><ThunderboltOutlined /> Salary sacrifice & benefits</span>
                ),
                children: (
                  <SectionCard id="sacrifice" title="Salary sacrifice & benefits">
                    <Paragraph>Childcare/Cycle-to-Work/EV reduce base salary for calculations; company car (P11D) adds taxable benefit.</Paragraph>
                    <ExampleBlock
                      description="Worked example"
                      exampleLines={[
                        'Base £60,000; Childcare £200/m; Cycle £1,000/yr; EV £400/m; Car P11D £3,000',
                        'Adjusted Base = 60,000 − (2,400 + 1,000 + 4,800) = £51,800',
                        'Taxable Benefits add P11D: +£3,000',
                      ]}
                      onCopy={() => copyToWizard({ baseSalary: 60000, childcareVouchers: 2400, cycleToWork: 1000, electricCar: 4800, companyCarBenefit: 3000 })}
                    />
                  </SectionCard>
                ),
              }
            ]}
          />
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <SectionCard title="Live tax constants" subtitle={`Tax Year ${selectedTaxYear}`}>
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                <VariableTable compact taxYear={selectedTaxYear} isScottish={isTaxResidentInScotland} />
              </div>
            </SectionCard>
            <SectionCard title="Disclaimer">
              <ul style={{ marginLeft: 18, marginBottom: 8 }}>
                <li>This tool is for guidance and education. It may not reflect all personal circumstances.</li>
                <li>Tax rules change; bands/thresholds may be updated during the year.</li>
                <li>No financial advice is provided. Verify results with official guidance or a professional.</li>
              </ul>
            </SectionCard>
            <SectionCard title="Sources & versioning">
              <ul style={{ marginLeft: 18, marginBottom: 8 }}>
                <li>
                  <a href="https://www.gov.uk/income-tax-rates" target="_blank" rel="noreferrer">HMRC: Income Tax rates and allowances (UK)</a>
                </li>
                <li>
                  <a href="https://www.gov.scot/publications/scottish-income-tax-2024-to-2025-rates-and-thresholds/" target="_blank" rel="noreferrer">Scottish Government: Scottish income tax rates and thresholds</a>
                </li>
                <li>
                  <a href="https://www.gov.uk/national-insurance-rates-letters" target="_blank" rel="noreferrer">HMRC: National Insurance rates and thresholds</a>
                </li>
                <li>
                  <a href="https://www.gov.uk/repaying-your-student-loan/what-you-pay" target="_blank" rel="noreferrer">GOV.UK: Student loan repayment thresholds and rates</a>
                </li>
                <li>
                  <a href="https://www.gov.uk/tax-employee-share-schemes" target="_blank" rel="noreferrer">HMRC: Tax and employee share schemes (incl. RSUs)</a>
                </li>
                <li>Automated tests cover RSUs, salary sacrifice, student loans, and regimes</li>
              </ul>
              <Button size="small" href="#" onClick={(e) => e.preventDefault()}>View test coverage summary</Button>
            </SectionCard>
          </Space>
        </Col>
      </Row>
    </div>
  );
}

export default Methodology;
